import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Text
} from "recharts";
import { formatNumberWithCommas, getBNMNOrKValueFromMillions } from '../../../utils/utils';

// function analyzeData(data) {
//   let maxTotal = -Infinity;
//   let negativeSums = [];
  
//   data.forEach(obj => {
//     // Convert total to number and find max
//     const total = parseFloat(obj.total);
//     if (total > maxTotal) {
//       maxTotal = total;
//     }
    
//     // Sum negative values in the object
//     let negativeSum = 0;
//     for (let key in obj) {
//       if (key !== 'name' && key !== 'total' && typeof obj[key] === 'number' && obj[key] < 0) {
//         negativeSum += obj[key];
//       }
//     }
//     negativeSums.push({ name: obj.name, negativeSum });
//   });
  
//   // Find the most negative sum
//   const mostNegative = negativeSums.reduce((min, curr) => 
//     curr.negativeSum < min.negativeSum ? curr : min, 
//     negativeSums[0] || { name: '', negativeSum: 0 }
//   );
//   if(maxTotal<0){
//     maxTotal=0;
//   }
//   const minTotal =  mostNegative['negativeSum']
  
//   return {
//     maxTotal,
//     minTotal
//   };
// }

function analyzeDataV2(data) {
  let maxTotal = -Infinity;
  let negativeSums = [];
  let positiveSums = [];
  
  data.forEach(obj => {
    // Convert total to number and find max
    const total = parseFloat(obj.total);
    if (total > maxTotal) {
      maxTotal = total;
    }
    
    // Sum negative values in the object
    let negativeSum = 0;
    // Sum positive values in the object
    let positiveSum = 0;
    
    for (let key in obj) {
      if (key !== 'name' && key !== 'total' && typeof obj[key] === 'number') {
        if (obj[key] < 0) {
          negativeSum += obj[key];
        } else if (obj[key] > 0) {
          positiveSum += obj[key];
        }
      }
    }
    
    negativeSums.push({ name: obj.name, negativeSum });
    positiveSums.push({ name: obj.name, positiveSum });
  });
  
  // Find the most negative sum
  const mostNegative = negativeSums.reduce((min, curr) => 
    curr.negativeSum < min.negativeSum ? curr : min, 
    negativeSums[0] || { name: '', negativeSum: 0 }
  );
  
  // Find the most positive sum
  const mostPositive = positiveSums.reduce((max, curr) => 
    curr.positiveSum > max.positiveSum ? curr : max, 
    positiveSums[0] || { name: '', positiveSum: 0 }
  );
  
  if (maxTotal < 0) {
    maxTotal = 0;
  }
  
  const minValue = mostNegative['negativeSum'];
  const maxValue = mostPositive['positiveSum'];
  
  return {
    minValue,
    maxValue
  };
}

// Example usage:
const data = [/* your array here */];

const SpendStackedBarChart = ({ categories=[], data=[], isVeridionChart=false, chartOrientation="positive", XAxisLabel="",isSpendCategoryChart=false }) => {
  
  const [hoveredBar, setHoveredBar] = useState(null);
  const handleMouseEnter = (dataKey, index, value) => {
    setHoveredBar({ dataKey, index, value });
  };
  const handleMouseLeave = () => {
    setHoveredBar(null);
  };
  const CustomLabel = ({ x, y, width, height, value, isHovered }) => {
    const displayedValue = hoveredBar?.dataKey + ": " + hoveredBar?.value;
    if (!isHovered) return null;
    const cx = x + width / 2;
    const cy = y - 10;
    const textColor = "#000";

    return (
      <>
        <rect
          x={cx - (displayedValue.length * 3.5) / 2}
          y={cy - 10}
          width={displayedValue.length * 7}
          height={20}
          fill="white"
          rx={4}
          ry={4}
        />
        <Text
          x={cx}
          y={cy}
          fill={textColor}
          fontSize={12}
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            whiteSpace: "break-spaces",
            fontSize: 10,
            zIndex: 10,
            pointerEvents: "none"
          }}
        >
          {displayedValue}
        </Text>
      </>
    );
  };
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const filteredData = payload.filter(
        (item) =>
          item.value !== 0 &&
          item.value !== null &&
          item.dataKey === hoveredBar?.dataKey
      );
      if (filteredData.length > 0) {
        return (
          <div
            style={{
              backgroundColor: "white",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "10px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
            }}
          >
            {
            filteredData.map((data, index) => {
            // const split = data?.name?.split("-");
            const entity = data?.name || "";
            const lastDashIndex = entity.lastIndexOf("-");
            const categoryName = entity.substring(0, lastDashIndex);
            const sectorName = entity.substring(lastDashIndex + 1);
            return (
              <div key={index}>
                <div>{`${sectorName}`}</div>
                <p style={{ color: data.fill, margin: 0 }}>
                  {`${categoryName}: ${formatNumberWithCommas(data.value)}`}{isVeridionChart?"":"MN"}
                </p>
              </div>
            );
          })}
          </div>
        );
      }
    }
    return null;
  };

  // Create flat list of all category+name combos and their corresponding color
  const uniqueKeys = categories.map((cat) => ({
    dataKey: `${cat.name}-${cat.category}`,
    name: cat.name,
    category: cat.category,
    color: cat.color
  }));

  // Prepare data for the chart
  const processedData = data.map((row) => {
    const base = { name: row.category };
    categories.forEach((cat) => {
      if (cat.category === row.category && row[cat.name] !== undefined) {
        base[`${cat.name}-${cat.category}`] = row[cat.name];
      }
    });

    // Add total for LabelList
    if(isVeridionChart) {
      base.total = Object.entries(base)
        .filter(([key]) => key !== "name")
        .reduce((acc, [, val]) => acc + (val || 0), 0)
        .toFixed(0);
    } else {
      base.total = Object.entries(base)
        .filter(([key]) => key !== "name")
        .reduce((acc, [, val]) => acc + (val || 0), 0)
        .toFixed(2);

    }

    return base
  });

  const barSize = 40;
  const chartHeight = data.length<3?data.length * 120:data.length * 80;
  
  function findLowestNegativeTotal(data) {
    let lowestValue = 0;
    data.forEach(item => {
      if (item.total) {
        const totalValue = parseFloat(item.total);
        if (totalValue < lowestValue) {
          lowestValue = totalValue;
        }
      }
    });
    
    return lowestValue;
  }

  function generateTicks(minNegativeValue) {
    const numberOfTicks = 5;
    const tickInterval = minNegativeValue / (numberOfTicks - 1);
    const ticks = [];

    for (let i = 0; i < numberOfTicks; i++) {
        ticks.push((i * tickInterval));
    }

    return ticks;
  }
  // const generateTicksForSpend = (start, end, totalTicks) => {
  //   const range = end - start;
  //   const stepSize = range / (totalTicks - 1);
  //   const ticks = [];

  //   for (let i = 0; i < totalTicks; i++) {
  //     const tick = start + stepSize * i;
  //     if (!ticks.includes(Math.round(tick))) {
  //       ticks.push(Math.round(tick));
  //     }
  //   }

  //   // Ensure zero is included in ticks
  //   if (!ticks.includes(0)) {
  //     ticks.push(0);
  //     ticks.sort((a, b) => a - b);
  //   }

  //   return ticks;
  // };
  // const generateDynamicTicksForSpend = (start, end, numTicks) => {
  //   // Define initial variables and step sizes
  //   const negativeTick = start;
  //   const stepSize = (end - 0) / (numTicks - 2); // Excluding negative tick and zero
  //   const ticks = [negativeTick, 0]; // Initialize the ticks array with negative and zero
  
  //   // Generate the positive ticks
  //   for (let i = 1; i < numTicks - 1; i++) {
  //     ticks.push(Math.round(stepSize * i));
  //   }
  
  //   // Add the end tick
  //   ticks.push(end);
  
  //   return ticks;
  // };

  const generateDynamicTicksForSpendV2 = (start, end, numTicks) => {
    // Define initial variables and step sizes
    const negativeTick = start;
    const stepSize = (end - 0) / (numTicks - 2); // Excluding negative tick and zero
    
    // If stepSize is smaller than the magnitude of negativeTick
    if (stepSize < Math.abs(negativeTick) && negativeTick<0 && stepSize>0) {
        const result = [0];
        let current = -stepSize;
        
        while (current >= negativeTick) {
            result.push(current);
            current -= stepSize;
        }
        
        if (result[result.length - 1] !== negativeTick) {
            result.push(negativeTick);
        }
        
        // Generate positive ticks and add to result
        for (let i = 1; i < numTicks - 1; i++) {
            result.push(Math.round(stepSize * i));
        }
        
        // Add end tick
        if (result[result.length - 1] !== end) {
            result.push(end);
        }
        
        return result.sort((a, b) => a - b); // Return sorted result
    } else {
        // Original logic for when stepSize >= abs(negativeTick)
        const ticks = [negativeTick, 0];
        
        // Generate the positive ticks
        for (let i = 1; i < numTicks - 1; i++) {
            ticks.push(Math.round(stepSize * i));
        }
        
        // Add the end tick
        ticks.push(end);
        
        return ticks;
    }
};

  const roundUpToNearest20 = (value) => Math.floor(value / 20) * 20;
  const roundUpToNearest500Negative = (value) => Math.floor(value / 500) * 500;
  const roundUpToNearest500Positive = (value) => Math.ceil(value / 500) * 500;
  const roundUpToNearest100Negative = (value) => Math.floor(value / 100) * 100;
  let lowestNegativeTotal = findLowestNegativeTotal(processedData);
  // lowestNegativeTotal = lowestNegativeTotal>=-4?-4:lowestNegativeTotal;
  // const minvalue = roundUpToNearest20(lowestNegativeTotal*1.2);
  const minvalue = lowestNegativeTotal>=-4?-4:roundUpToNearest20(lowestNegativeTotal*1.2);
  const ticks = generateTicks(minvalue);

  let spendDomainPositive =0;
  let spendDomainNegative =0;
  let spendTicks = [];
  
  if(isSpendCategoryChart) {
    // console.log("processedData",processedData)
    const spendDomain = analyzeDataV2(processedData);
    // console.log("spendDomain", spendDomain);
    console.log(roundUpToNearest500Negative(spendDomain.minValue), roundUpToNearest500Positive(spendDomain.maxValue));
    spendDomainPositive = roundUpToNearest500Positive(spendDomain.maxValue);
    spendDomainNegative = spendDomainPositive>2000?roundUpToNearest500Negative(spendDomain.minValue):roundUpToNearest100Negative(spendDomain.minValue);
    // const spendTicks= generateTicksForSpend(roundUpToNearest500Negative(spendDomain.minTotal), roundUpToNearest500Positive(spendDomain.maxTotal),5);
    // const spendTicks = generateDynamicTicksForSpend(roundUpToNearest500Negative(spendDomain.minTotal), roundUpToNearest500Positive(spendDomain.maxTotal), 6);
    // const spendTicks = generateDynamicTicksForSpendV2(roundUpToNearest500Negative(spendDomain.minTotal), roundUpToNearest500Positive(spendDomain.maxTotal), 6);
    spendTicks = generateDynamicTicksForSpendV2(spendDomainNegative, spendDomainPositive, 6);
   
  }
  

  return (
    <div style={{ width: "100%", maxHeight: "450px", overflowY: "auto" }}>
    <div style={{ width: "100%", height: chartHeight }}> 
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={processedData}
          margin={{ top: 20, right: 120, left: 60, bottom: 20 }}
          stackOffset="sign"
        >
          <CartesianGrid horizontal={false} vertical stroke="#DFDFDF" />
          <YAxis
            dataKey="name"
            type="category"
            tickLine={false}
            width={80}
            orientation={chartOrientation=="negative"?"right":"left"}
          />
          
          { chartOrientation=="negative" && 
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              ticks = {ticks}
              tickFormatter={(value) => formatNumberWithCommas(value)} 
              domain={[
                dataMin => minvalue,
                dataMax => 0
              ]}
            />
          }
          { chartOrientation=="positive" && isSpendCategoryChart==true && spendDomainNegative<0 &&
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              domain={[spendDomainNegative, spendDomainPositive]}
              ticks = {spendTicks}
              tickFormatter={(value) => formatNumberWithCommas(value)} 
              label={
                XAxisLabel ? {
                  value: XAxisLabel,
                  textAnchor: "middle",
                  dy: 20,
                  style: { fontSize: "16px", fontWeight: "700", fill: "#616161" }
                } : null
              }
            />
          }
          { chartOrientation=="positive" && isSpendCategoryChart==true && spendDomainNegative>=0 &&
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => formatNumberWithCommas(value)} 
              label={
                XAxisLabel ? {
                  value: XAxisLabel,
                  textAnchor: "middle",
                  dy: 20,
                  style: { fontSize: "16px", fontWeight: "700", fill: "#616161" }
                } : null
              }
            />
          }
          
          { chartOrientation=="positive" && isSpendCategoryChart==false && 
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => formatNumberWithCommas(value)} 
              label={
                XAxisLabel ? {
                  value: XAxisLabel,
                  textAnchor: "middle",
                  dy: 20,
                  style: { fontSize: "16px", fontWeight: "700", fill: "#616161" }
                } : null
              }
            />
          }
          <Tooltip content={<CustomTooltip />} cursor={false} />

          {uniqueKeys.map(({ dataKey, color },index) => (
            <Bar
              key={dataKey}
              dataKey={dataKey}
              stackId="a"
              fill={color}
              barSize={barSize}
             // stroke="#ffffff"
             // strokeWidth={1}
              onMouseEnter={(e, index) =>
                handleMouseEnter(dataKey, index, e[dataKey])
              }
              onMouseLeave={handleMouseLeave}
            >
              {/* {dataKey === uniqueKeys[uniqueKeys.length - 1].dataKey && (
                <LabelList
                  dataKey="total"
                  position="right"
                  style={{ 
                    fill: '#333',
                    fontSize: 12,
                    fontWeight: 'bold',
                    textAnchor: 'start'
                  }}
                  formatter={(value) => formatNumberWithCommas(parseFloat(value))}
                  offset={10}
                />
              )} */}

               {/* Only add LabelList on the LAST bar of the stack */}
                {/* {index === uniqueKeys.length - 1 && (
                  <LabelList
                    dataKey="total"  // pull totals from processedData
                    position="right"
                    content={(props) => {
                      const { x, y, value } = props;
                      console.log(props)
                      return (
                        <text
                          x={x - 10}
                          y={y + 18}
                          fill="#000"
                          fontSize={14}
                          fontWeight="bold"
                          textAnchor="end"
                          dominantBaseline="central"
                        >
                          {formatNumberWithCommas(value)}
                        </text>
                      );
                    }}
                  />
                )} */}
            </Bar>
          ))}

          { chartOrientation=="negative" && 
            <Bar dataKey="total" stackId="a" fill="transparent" barSize={barSize} display={"none"}>
                <LabelList
                  dataKey="total"
                  position="right"
                  // formatter={(val) => `${val}`}
                  style={{ fontWeight: "bold" }}
                  content={(props) => {
                    const { x, y, width, value } = props;
                    const positionX = x-10;
                    const positionY = y+18;
                    return (
                      <text
                        x={positionX}
                        y={positionY}
                        fill="rgb(170, 0, 26)"
                        fontSize={14}
                        fontWeight="bold"
                        textAnchor={"end"}
                        dominantBaseline="central"
                      >
                        {formatNumberWithCommas(value)}
                        
                      </text>
                    );
                  }}
                />
            </Bar>
          }
          {
          chartOrientation=="positive" && 
            <Bar dataKey="temp" stackId="a" fill="transparent" barSize={barSize}>
              <LabelList
                dataKey="total"
                position="right"
                offset={10}
                style={{ fontWeight: 900, fill: "#3A3A3A" }}
                content={(props) => {
                const { x, y, width, value } = props;
                const positionX = x + width + 10;;
                const positionY = y+18;
                  return (
                    <text
                      x={positionX}
                      y={positionY}
                      fill="#000"
                      fontSize={14}
                      fontWeight="bold"
                      textAnchor={"start"}
                      dominantBaseline="central"
                    >
                      {formatNumberWithCommas(value)}
                      
                    </text>
                  );
                }}
              /> 
            </Bar>
          }
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>  
  );
};

export default SpendStackedBarChart;
